import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Lock, Check, Loader2, Sparkles, Palette, Zap, Activity,
} from 'lucide-react';
import {
  fetchUnlocks, setActiveSkin,
  type UnlockCard,
} from '@/lib/api';
import styles from './UnlocksPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Skin catalogue. Phase: theme milestones.
 *
 * Replaces the points-trade purchase model with charging milestones.
 * Points stay liquid for charge-session redemption — themes become
 * aspirational. Fleet drivers see every skin pre-unlocked via
 * Phase 3a's fleet attribution flow (unchanged).
 *
 * UI states per skin:
 *   • Owned + active           → "Applied" badge + Revert button
 *   • Owned, not active        → Apply button
 *   • Locked (kwh milestone)   → progress bar + "{user} / {threshold} kWh"
 *   • Locked (session "")      → progress bar + "{user} / {threshold} sessions"
 *   • Locked (fleet_only)      → "Available for fleet members" subtext
 *   • Locked (no criteria, admin_only) → hidden from non-super_admin
 *     (server already filters; this is defense in depth)
 *
 * No Buy button. The /unlocks/:code/purchase route now refuses
 * skin purchases with POINTS_TRADE_DEPRECATED — but the better
 * defense is not showing the affordance.
 */
export function UnlocksPage({ onNavigate }: Props) {
  const { t } = useTranslation('unlocks');
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['unlocks', 'list'],
    queryFn: fetchUnlocks,
    staleTime: 30_000,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['unlocks'] });
    qc.invalidateQueries({ queryKey: ['gamification'] });
  };

  if (q.isLoading && !q.data) {
    return <div className={styles.page}><p className={styles.loading}>{t('loading')}</p></div>;
  }
  if (!q.data) return null;

  const { unlocks, totalPoints, currentTier, userTotalKwh, userTotalSessions, isFleetMember } = q.data;
  const skins = unlocks.filter((u) => u.category === 'skin');

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/profile')} aria-label={t('back')}>
        <ArrowLeft size={18} />
      </button>

      <header className={styles.header}>
        <div className={styles.headerIcon}><Sparkles size={20} /></div>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {isFleetMember
              ? t('subtitle.fleet')
              : t('subtitle.default')}
          </p>
        </div>
        <div className={styles.balanceChip}>
          <span className={styles.balanceAmt}>{totalPoints.toLocaleString()}</span>
          <span className={styles.balanceLabel}>{t('pointsUnit')}</span>
        </div>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionLabel}>
          <Palette size={12} /> {t('sectionLabel')}
        </h2>
        <div className={styles.grid}>
          {skins.map((u) => (
            <SkinCard
              key={u.code}
              unlock={u}
              currentTier={currentTier}
              userTotalKwh={userTotalKwh}
              userTotalSessions={userTotalSessions}
              onChange={refresh}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function SkinCard({
  unlock, currentTier, userTotalKwh, userTotalSessions, onChange,
}: {
  unlock: UnlockCard;
  currentTier: string;
  userTotalKwh: number;
  userTotalSessions: number;
  onChange: () => void;
}) {
  const { t } = useTranslation('unlocks');
  const [busy, setBusy] = useState<'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accent = (unlock.metadata['accentColor'] as string | undefined) ?? '#888';
  const charging = (unlock.metadata['chargingColor'] as string | undefined) ?? accent;

  const handleApply = async () => {
    setBusy('apply'); setError(null);
    try { await setActiveSkin(unlock.code); onChange(); }
    catch (err: unknown) {
      setError((err as { message?: string }).message ?? t('error.applyFailed'));
    } finally { setBusy(null); }
  };
  const handleRevert = async () => {
    setBusy('apply'); setError(null);
    try { await setActiveSkin(null); onChange(); }
    catch (err: unknown) {
      setError((err as { message?: string }).message ?? t('error.revertFailed'));
    } finally { setBusy(null); }
  };

  const tierBlocked = !unlock.tierEligible;

  return (
    <div className={`${styles.card} ${unlock.active ? styles.cardActive : ''}`}>
      <div
        className={styles.swatch}
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, ${charging} 100%)`,
        }}
        aria-hidden
      >
        {unlock.active && (
          <span className={styles.appliedBadge}>
            <Check size={12} /> {t('badge.applied')}
          </span>
        )}
        {!unlock.owned && (
          <span className={styles.appliedBadge} style={{ background: 'rgba(0,0,0,0.55)' }}>
            <Lock size={12} /> {t('badge.locked')}
          </span>
        )}
      </div>

      <div className={styles.cardBody}>
        <p className={styles.cardName}>{unlock.name}</p>
        <p className={styles.cardDesc}>{unlock.description}</p>

        {unlock.tierMinimum && (
          <p className={`${styles.tierLine} ${tierBlocked ? styles.tierLineBlocked : ''}`}>
            <Lock size={10} /> {t('tier.requirement', { tier: unlock.tierMinimum })}
            {tierBlocked && <span className={styles.tierCurrent}> {t('tier.current', { tier: currentTier })}</span>}
          </p>
        )}

        {/* Locked-with-milestone progress bar. */}
        {!unlock.owned && unlock.unlockCriteriaKind === 'kwh_milestone' && unlock.unlockCriteriaThreshold !== null && (
          <MilestoneProgress
            icon={<Zap size={11} />}
            unit="kWh"
            current={userTotalKwh}
            threshold={unlock.unlockCriteriaThreshold}
            percent={unlock.progressPercent ?? 0}
          />
        )}
        {!unlock.owned && unlock.unlockCriteriaKind === 'session_milestone' && unlock.unlockCriteriaThreshold !== null && (
          <MilestoneProgress
            icon={<Activity size={11} />}
            unit={t('milestone.unitSessions')}
            current={userTotalSessions}
            threshold={unlock.unlockCriteriaThreshold}
            percent={unlock.progressPercent ?? 0}
          />
        )}
        {!unlock.owned && unlock.unlockCriteriaKind === 'fleet_only' && (
          <p className={styles.tierLine}>
            <Lock size={10} /> {t('fleetOnly')}
          </p>
        )}

        <div className={styles.cardFoot}>
          {unlock.owned && !unlock.active && (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleApply}
              disabled={busy !== null}
            >
              {busy === 'apply'
                ? <><Loader2 size={12} className={styles.spinner} /> …</>
                : t('actions.apply')}
            </button>
          )}
          {unlock.owned && unlock.active && (
            <button
              type="button"
              className={styles.ghostBtn}
              onClick={handleRevert}
              disabled={busy !== null}
            >
              {busy === 'apply'
                ? <><Loader2 size={12} className={styles.spinner} /> …</>
                : t('actions.revert')}
            </button>
          )}
        </div>

        {error && <p className={styles.error}>{error}</p>}
      </div>
    </div>
  );
}

/**
 * Progress bar + "{current} / {threshold} {unit} ({pct}%)" copy
 * under a locked milestone skin. Uses the brand accent (--evi-accent
 * #21c163) at 60% width for the fill, surface-tinted track.
 */
function MilestoneProgress({
  icon, unit, current, threshold, percent,
}: {
  icon: React.ReactNode;
  unit: string;
  current: number;
  threshold: number;
  percent: number;
}) {
  return (
    <div style={{ marginTop: 8 }}>
      <p style={{
        fontSize: 11,
        color: 'var(--color-tx-m, #888)',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        margin: '0 0 4px',
      }}>
        {icon}
        <span>
          {current.toLocaleString()} / {threshold.toLocaleString()} {unit} ({percent}%)
        </span>
      </p>
      <div style={{
        height: 4,
        background: 'color-mix(in srgb, var(--color-tx-m, #888) 18%, transparent)',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
        <div
          style={{
            height: '100%',
            width: `${percent}%`,
            background: 'var(--evi-accent, #21c163)',
            transition: 'width 300ms ease',
          }}
        />
      </div>
    </div>
  );
}
